import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../../components/auth/AuthContext';
import Toast from '../../components/common/Toast';
import UploadingModal from '../../components/common/UploadingModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import GenerateDocumentModal from '../../components/customDocuments/GenerateDocumentModal';
import BatchGenerateModal from '../../components/customDocuments/BatchGenerateModal';
import customDocumentService from '../../services/api/customDocumentService';
import { clientService } from '../../services/clientService';
import type {
  DocumentTemplateSummary,
  DocumentAuditRecord,
  DocumentGenerationResult,
  BatchJobStatus,
} from '../../types/customDocuments';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import '../../styles/FilterButtons.css';
import '../../components/customDocuments/CustomDocuments.css';

const MAX_SELECTION = 10;

const CustomDocuments: React.FC = () => {
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;
  const { t, i18n } = useTranslation();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // Trades grid
  const [trades, setTrades] = useState<any[]>([]);
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [tradesError, setTradesError] = useState<string | null>(null);
  const [selectedTradeIds, setSelectedTradeIds] = useState<string[]>([]);

  // Date range filter — default empty (show all trades)
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Filter state (Set<string> pattern — same as ForwardMaturity)
  const [selectedOurParties, setSelectedOurParties] = useState<Set<string>>(new Set());
  const [selectedCounterparties, setSelectedCounterparties] = useState<Set<string>>(new Set());
  const [selectedProductTypes, setSelectedProductTypes] = useState<Set<string>>(new Set());
  const [selectedWorkflowStates, setSelectedWorkflowStates] = useState<Set<string>>(new Set());
  const [selectedSettlementTypes, setSelectedSettlementTypes] = useState<Set<string>>(new Set());
  const [selectedTradeTypes, setSelectedTradeTypes] = useState<Set<string>>(new Set());

  // Templates (active only)
  const [templates, setTemplates] = useState<DocumentTemplateSummary[]>([]);

  // Generated documents
  const [auditRecords, setAuditRecords] = useState<DocumentAuditRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Batch jobs
  const [batchJobs, setBatchJobs] = useState<BatchJobStatus[]>([]);
  const [selectedBatchJobId, setSelectedBatchJobId] = useState<string | null>(null);
  const [batchJobsExpanded, setBatchJobsExpanded] = useState(true);

  // Busy overlay (download / delete)
  const [busyMessage, setBusyMessage] = useState<string | null>(null);

  // Delete confirm (single)
  const [deleteRecordId, setDeleteRecordId] = useState<string | null>(null);

  // Delete All confirm
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  // Toast
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [showToast, setShowToast] = useState(false);

  const showToastNotification = useCallback(
    (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
      setToastMessage(message);
      setToastType(type);
      setShowToast(true);
    },
    []
  );

  // ========== Data Loading ==========

  const loadTrades = useCallback(async () => {
    if (!clientId) return;
    setLoadingTrades(true);
    setTradesError(null);
    try {
      const data = await clientService.getUnmatchedTrades(clientId, true, undefined, undefined, true);
      setTrades(data);
      setSelectedTradeIds([]);
    } catch {
      setTradesError(t('customDocumentsPage.errors.loadTradesFailed'));
    } finally {
      setLoadingTrades(false);
    }
  }, [clientId, t]);

  const loadTemplates = useCallback(async () => {
    if (!clientId) return;
    try {
      const all = await customDocumentService.listTemplates(clientId);
      setTemplates(all.filter(tpl => tpl.isActive));
    } catch {
      // Non-fatal — modal will show empty dropdown
    }
  }, [clientId]);

  const loadAuditRecords = useCallback(async () => {
    if (!clientId) return;
    setLoadingRecords(true);
    try {
      const records = await customDocumentService.listAuditRecords(clientId);
      setAuditRecords(records);
    } catch {
      showToastNotification(t('customDocumentsPage.errors.loadRecordsFailed'), 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [clientId, showToastNotification, t]);

  const loadBatchJobs = useCallback(async () => {
    if (!clientId) return;
    try {
      const jobs = await customDocumentService.listBatchJobs(clientId);
      setBatchJobs(jobs);
    } catch {
      // Non-fatal
    }
  }, [clientId]);

  useEffect(() => { loadTrades(); }, [loadTrades]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadAuditRecords(); }, [loadAuditRecords]);
  useEffect(() => { loadBatchJobs(); }, [loadBatchJobs]);

  // ========== Unique filter values (derived from loaded trades) ==========

  const uniqueOurParties = useMemo(() =>
    Array.from(new Set(trades.map((t: any) => t.OurParty).filter(Boolean))).sort()
  , [trades]);

  const uniqueCounterparties = useMemo(() =>
    Array.from(new Set(trades.map((t: any) => t.CounterpartyName).filter(Boolean))).sort()
  , [trades]);

  const uniqueProductTypes = useMemo(() =>
    Array.from(new Set(trades.map((t: any) => t.ProductType).filter(Boolean))).sort()
  , [trades]);

  const uniqueWorkflowStates = useMemo(() =>
    Array.from(new Set(trades.map((t: any) => t.workflowState).filter(Boolean))).sort()
  , [trades]);

  const uniqueSettlementTypes = useMemo(() =>
    Array.from(new Set(trades.map((t: any) => t.SettlementType).filter(Boolean))).sort()
  , [trades]);

  // ========== Filter toggle helpers ==========

  const toggleFilter = useCallback((setter: React.Dispatch<React.SetStateAction<Set<string>>>, value: string) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setSelectedOurParties(new Set());
    setSelectedCounterparties(new Set());
    setSelectedProductTypes(new Set());
    setSelectedWorkflowStates(new Set());
    setSelectedSettlementTypes(new Set());
    setSelectedTradeTypes(new Set());
  }, []);

  // ========== Filtered trades (all filters applied client-side) ==========

  const filteredTrades = useMemo(() => {
    return trades.filter((trade: any) => {
      // Date range
      if (dateFrom || dateTo) {
        const parts = trade.TradeDate?.split('-');
        if (parts && parts.length >= 3) {
          const tradeDate = parts[0].length === 4
            ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
            : new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
          if (dateFrom && tradeDate < new Date(dateFrom)) return false;
          if (dateTo && tradeDate > new Date(dateTo + 'T23:59:59')) return false;
        }
      }
      // Our Party
      if (selectedOurParties.size > 0 && !selectedOurParties.has(trade.OurParty)) return false;
      // Counterparty
      if (selectedCounterparties.size > 0 && !selectedCounterparties.has(trade.CounterpartyName)) return false;
      // Product Type
      if (selectedProductTypes.size > 0 && !selectedProductTypes.has(trade.ProductType)) return false;
      // Workflow State
      if (selectedWorkflowStates.size > 0 && !selectedWorkflowStates.has(trade.workflowState)) return false;
      // Settlement Type
      if (selectedSettlementTypes.size > 0 && !selectedSettlementTypes.has(trade.SettlementType)) return false;
      // Internal / External / Splits
      if (selectedTradeTypes.size > 0) {
        const isInternal = !!trade.isInternalTrade;
        const isSplit = !!trade.isSplitTrade;
        const matchesInternal = selectedTradeTypes.has('Internal') && isInternal;
        const matchesExternal = selectedTradeTypes.has('External') && !isInternal && !isSplit;
        const matchesSplits = selectedTradeTypes.has('Splits') && isSplit;
        if (!matchesInternal && !matchesExternal && !matchesSplits) return false;
      }
      return true;
    });
  }, [trades, dateFrom, dateTo, selectedOurParties, selectedCounterparties, selectedProductTypes, selectedWorkflowStates, selectedSettlementTypes, selectedTradeTypes]);

  // ========== Document count per trade (from audit records) ==========

  const docCountsByTradeId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const record of auditRecords) {
      counts.set(record.tradeId, (counts.get(record.tradeId) || 0) + 1);
    }
    return counts;
  }, [auditRecords]);

  // ========== Grid ref (for Select All / Deselect All) ==========

  const gridRef = useRef<AgGridReact>(null);

  // ========== Docs grid ref + selection ==========

  const docsGridRef = useRef<AgGridReact>(null);
  const [selectedDocRecords, setSelectedDocRecords] = useState<DocumentAuditRecord[]>([]);

  const handleDocsSelectionChanged = useCallback((event: any) => {
    setSelectedDocRecords(event.api.getSelectedRows());
  }, []);

  // ========== Documents search ==========

  const [docsSearchText, setDocsSearchText] = useState('');

  // ========== Selection ==========

  const handleSelectionChanged = useCallback((event: any) => {
    const rows = event.api.getSelectedRows();
    setSelectedTradeIds(rows.map((row: any) => row.id));
  }, []);

  const selectionOverLimit = selectedTradeIds.length > MAX_SELECTION;
  const canGenerate = selectedTradeIds.length > 0 && !selectionOverLimit;

  // ========== Filtered Audit Records ==========

  const filteredAuditRecords = useMemo(() => {
    let records = auditRecords;
    // Filter by batch job if one is selected
    if (selectedBatchJobId) {
      records = records.filter(r => r.batchJobId === selectedBatchJobId);
    }
    // Filter by trade selection
    if (selectedTradeIds.length > 0) {
      const idSet = new Set(selectedTradeIds);
      records = records.filter(r => idSet.has(r.tradeId));
    }
    return records;
  }, [selectedTradeIds, auditRecords, selectedBatchJobId]);

  // ========== Download helper (signed URL now includes Content-Disposition: attachment) ==========

  const triggerDownload = useCallback((signedUrl: string, filename: string) => {
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // ========== Download ==========

  const handleDownload = useCallback(async (record: DocumentAuditRecord) => {
    if (!clientId) return;
    setBusyMessage(t('customDocumentsPage.downloading'));
    try {
      const { signedUrl, filename } = await customDocumentService.getDownloadUrl(
        clientId,
        record.recordId
      );
      triggerDownload(signedUrl, filename || `document-${record.recordId}`);
    } catch {
      showToastNotification(t('customDocumentsPage.errors.downloadFailed'), 'error');
    } finally {
      setBusyMessage(null);
    }
  }, [clientId, showToastNotification, t, triggerDownload]);

  // ========== Download Selected ==========

  const handleDownloadAll = useCallback(async () => {
    if (!clientId) return;
    const downloadable = selectedDocRecords.filter(r => r.status === 'SUCCESS');
    if (downloadable.length === 0) return;
    setBusyMessage(t('customDocumentsPage.downloading'));
    try {
      for (const record of downloadable) {
        const { signedUrl, filename } = await customDocumentService.getDownloadUrl(clientId, record.recordId);
        triggerDownload(signedUrl, filename || `document-${record.recordId}`);
        // Small delay between downloads to avoid browser throttling
        if (downloadable.indexOf(record) < downloadable.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    } catch {
      showToastNotification(t('customDocumentsPage.errors.downloadFailed'), 'error');
    } finally {
      setBusyMessage(null);
    }
  }, [clientId, selectedDocRecords, showToastNotification, t, triggerDownload]);

  // ========== Generation Callback ==========

  const handleGenerated = useCallback((_results: DocumentGenerationResult[]) => {
    loadAuditRecords();
    showToastNotification(t('customDocumentsPage.generateSuccess'), 'success');
  }, [loadAuditRecords, showToastNotification, t]);

  // ========== Batch Job Created ==========

  const handleBatchJobCreated = useCallback((result: { jobId: string; totalItems: number }) => {
    showToastNotification(
      t('batch.jobCreated', 'Batch job created for {{count}} trades', { count: result.totalItems }),
      'success'
    );
    loadBatchJobs();
  }, [loadBatchJobs, showToastNotification, t]);

  // ========== Delete ==========

  const handleDeleteRecord = useCallback(async () => {
    if (!clientId || !deleteRecordId) return;
    try {
      await customDocumentService.deleteAuditRecord(clientId, deleteRecordId);
      setDeleteRecordId(null);
      loadAuditRecords();
      showToastNotification(t('customDocumentsPage.deleteSuccess'), 'success');
    } catch {
      showToastNotification(t('customDocumentsPage.errors.deleteFailed'), 'error');
      setDeleteRecordId(null);
    }
  }, [clientId, deleteRecordId, loadAuditRecords, showToastNotification, t]);

  // ========== Delete All ==========

  const handleDeleteAll = useCallback(async () => {
    if (!clientId || selectedDocRecords.length === 0) return;
    setShowDeleteAllConfirm(false);
    setBusyMessage(t('customDocumentsPage.deleting', 'Deleting...'));
    try {
      let deletedCount = 0;
      for (const record of selectedDocRecords) {
        await customDocumentService.deleteAuditRecord(clientId, record.recordId);
        deletedCount++;
      }
      setSelectedDocRecords([]);
      loadAuditRecords();
      showToastNotification(
        t('customDocumentsPage.deleteSelectedSuccess', '{{count}} documents deleted', { count: deletedCount }),
        'success'
      );
    } catch {
      showToastNotification(t('customDocumentsPage.errors.deleteFailed'), 'error');
      loadAuditRecords();
    } finally {
      setBusyMessage(null);
    }
  }, [clientId, selectedDocRecords, loadAuditRecords, showToastNotification, t]);

  // ========== Column Definitions ==========

  const tradesColDefs = useMemo<ColDef[]>(() => [
    { field: 'workflowState', headerName: t('grid.columns.workflow', 'State'), minWidth: 100, flex: 1 },
    {
      field: 'TradeNumber',
      headerName: t('grid.columns.tradeNumber', 'Trade No.'),
      minWidth: 100,
      flex: 1,
      comparator: (valueA: string, valueB: string) => {
        const numA = parseInt(valueA, 10);
        const numB = parseInt(valueB, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        if (!isNaN(numA)) return -1;
        if (!isNaN(numB)) return 1;
        return (valueA || '').localeCompare(valueB || '');
      },
    },
    {
      headerName: t('customDocumentsPage.docsCount', 'Docs'),
      minWidth: 60,
      width: 70,
      maxWidth: 90,
      flex: 0,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) => docCountsByTradeId.get(params.data?.id) || 0,
      cellRenderer: (params: any) => {
        const count = params.value;
        if (!count) return <span className="cd-doc-count-empty">—</span>;
        return <span className="cd-doc-count-badge">{count}</span>;
      },
    },
    {
      headerName: t('grid.columns.parentTradeNumber', 'Parent Trade'),
      valueGetter: (params: any) => params.data?.internalTradeRef?.parentTradeNumber || '',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'TradeDate',
      headerName: t('grid.columns.tradeDate', 'Trade Date'),
      minWidth: 100,
      flex: 1,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: (filterDate: Date, cellValue: string) => {
          if (!cellValue) return -1;
          const parts = cellValue.split('-');
          if (parts.length < 3) return -1;
          const cellDate = parts[0].length === 4
            ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
            : new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
          if (cellDate < filterDate) return -1;
          if (cellDate > filterDate) return 1;
          return 0;
        },
      },
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        const parts = params.value.split('-');
        if (parts.length < 3) return params.value;
        if (parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return `${parts[0]}/${parts[1]}/${parts[2]}`;
      },
      comparator: (valueA: string, valueB: string) => {
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;
        const pA = valueA.split('-').map(Number);
        const pB = valueB.split('-').map(Number);
        const dateA = pA[0] > 1000 ? new Date(pA[0], pA[1] - 1, pA[2]) : new Date(pA[2], pA[1] - 1, pA[0]);
        const dateB = pB[0] > 1000 ? new Date(pB[0], pB[1] - 1, pB[2]) : new Date(pB[2], pB[1] - 1, pB[0]);
        return dateA.getTime() - dateB.getTime();
      },
    },
    { field: 'OurParty', headerName: t('grid.columns.ourParty', 'Our Party'), minWidth: 120, flex: 1.5 },
    { field: 'CounterpartyName', headerName: t('grid.columns.counterparty', 'Counterparty'), minWidth: 120, flex: 1.5 },
    { field: 'ProductType', headerName: t('grid.columns.productType', 'Type'), minWidth: 80, flex: 0.8 },
    { field: 'Direction', headerName: t('grid.columns.direction', 'Buy/Sell'), minWidth: 70, flex: 0.7 },
    { field: 'Currency1', headerName: t('grid.columns.currency1', 'Ccy 1'), minWidth: 60, flex: 0.6 },
    {
      field: 'QuantityCurrency1',
      headerName: t('grid.columns.amount', 'Amount'),
      minWidth: 100,
      flex: 1,
      valueFormatter: (params: any) =>
        params.value != null
          ? Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '',
    },
    { field: 'Currency2', headerName: t('grid.columns.currency2', 'Ccy 2'), minWidth: 60, flex: 0.6 },
    {
      field: 'Price',
      headerName: t('grid.columns.price', 'Rate'),
      minWidth: 80,
      flex: 0.8,
      valueFormatter: (params: any) =>
        params.value != null
          ? Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
          : '',
    },
  ], [t, docCountsByTradeId]);

  const docsColDefs = useMemo<ColDef[]>(() => [
    { field: 'tradeNumber', headerName: t('grid.columns.tradeNumber', 'Trade No.'), minWidth: 100, flex: 1 },
    { field: 'templateName', headerName: t('customDocumentsPage.docsGrid.template'), minWidth: 140, flex: 1.5 },
    {
      field: 'outputFormat',
      headerName: t('customDocumentsPage.docsGrid.format'),
      minWidth: 70,
      flex: 0.6,
      cellRenderer: (params: any) => (
        <span className={`output-format-badge ${params.value?.toLowerCase()}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'generatedAt',
      headerName: t('customDocumentsPage.docsGrid.generatedAt'),
      minWidth: 140,
      flex: 1.2,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      },
    },
    {
      field: 'triggeredByName',
      headerName: t('customDocumentsPage.docsGrid.by'),
      minWidth: 100,
      flex: 1,
      valueGetter: (params: any) =>
        params.data?.trigger === 'RULE' ? 'SYSTEM' : (params.data?.triggeredByName || params.data?.triggeredBy || '—'),
    },
    {
      field: 'status',
      headerName: t('customDocumentsPage.docsGrid.status'),
      minWidth: 80,
      flex: 0.7,
      filterValueGetter: (params) => {
        const isSuccess = params.data?.status === 'SUCCESS';
        return isSuccess
          ? t('customDocumentsPage.docsGrid.statusSuccess')
          : t('customDocumentsPage.docsGrid.statusFailed');
      },
      cellRenderer: (params: any) => {
        const isSuccess = params.value === 'SUCCESS';
        return (
          <span
            className={`status-badge ${isSuccess ? 'success' : 'failed'}`}
            title={!isSuccess ? (params.data?.errorMessage || '') : undefined}
          >
            {isSuccess
              ? t('customDocumentsPage.docsGrid.statusSuccess')
              : t('customDocumentsPage.docsGrid.statusFailed')}
          </span>
        );
      },
    },
    {
      headerName: t('customDocumentsPage.docsGrid.actions'),
      minWidth: 140,
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => (
        <div className="actions-cell">
          {params.data?.status === 'SUCCESS' && (
            <button className="cd-action-btn" onClick={() => handleDownload(params.data)}>
              {t('customDocumentsPage.docsGrid.downloadBtn')}
            </button>
          )}
          <button className="cd-action-btn cd-danger" onClick={() => setDeleteRecordId(params.data?.recordId)}>
            {t('customDocumentsPage.docsGrid.deleteBtn')}
          </button>
        </div>
      ),
    },
  ], [t, handleDownload]);

  // ========== Render ==========

  return (
    <div className="custom-docs-page">
      <div className="custom-docs-page-header">
        <h2 className="custom-docs-page-title">{t('customDocumentsPage.title')}</h2>
        <SystemDateDisplay clientId={clientId} />
      </div>

      {/* Filter Toolbar */}
      <div className="cd-filters-toolbar">
        {/* Trade Date */}
        <div className="cd-filter-group">
          <label className="cd-filter-group-label">{t('customDocumentsPage.filters.tradeDate', 'Trade Date')}:</label>
          <div className="cd-filter-buttons">
            <input type="date" className="date-filter-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <span className="cd-filter-group-label">–</span>
            <input type="date" className="date-filter-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>
        {/* Our Party */}
        {uniqueOurParties.length > 0 && (
          <div className="cd-filter-group">
            <label className="cd-filter-group-label">{t('customDocumentsPage.filters.ourParty', 'Our Party')}:</label>
            <div className="cd-filter-buttons">
              {uniqueOurParties.map(v => (
                <button key={v} className={`filter-btn ${selectedOurParties.has(v) ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedOurParties, v)}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {/* Counterparty */}
        {uniqueCounterparties.length > 0 && (
          <div className="cd-filter-group">
            <label className="cd-filter-group-label">{t('customDocumentsPage.filters.counterparty', 'Counterparty')}:</label>
            <div className="cd-filter-buttons">
              {uniqueCounterparties.map(v => (
                <button key={v} className={`filter-btn ${selectedCounterparties.has(v) ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedCounterparties, v)}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {/* Product Type */}
        {uniqueProductTypes.length > 0 && (
          <div className="cd-filter-group">
            <label className="cd-filter-group-label">{t('customDocumentsPage.filters.productType', 'Product Type')}:</label>
            <div className="cd-filter-buttons">
              {uniqueProductTypes.map(v => (
                <button key={v} className={`filter-btn ${selectedProductTypes.has(v) ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedProductTypes, v)}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {/* Workflow State */}
        {uniqueWorkflowStates.length > 0 && (
          <div className="cd-filter-group">
            <label className="cd-filter-group-label">{t('customDocumentsPage.filters.workflowState', 'Workflow State')}:</label>
            <div className="cd-filter-buttons">
              {uniqueWorkflowStates.map(v => (
                <button key={v} className={`filter-btn ${selectedWorkflowStates.has(v) ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedWorkflowStates, v)}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {/* Settlement Type */}
        {uniqueSettlementTypes.length > 0 && (
          <div className="cd-filter-group">
            <label className="cd-filter-group-label">{t('customDocumentsPage.filters.settlementType', 'Settlement Type')}:</label>
            <div className="cd-filter-buttons">
              {uniqueSettlementTypes.map(v => (
                <button key={v} className={`filter-btn ${selectedSettlementTypes.has(v) ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedSettlementTypes, v)}>{v}</button>
              ))}
            </div>
          </div>
        )}
        {/* Internal / External */}
        <div className="cd-filter-group">
          <label className="cd-filter-group-label">{t('customDocumentsPage.filters.tradeType', 'Trade Type')}:</label>
          <div className="cd-filter-buttons">
            <button className={`filter-btn ${selectedTradeTypes.has('Internal') ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedTradeTypes, 'Internal')}>{t('customDocumentsPage.filters.internal', 'Internal')}</button>
            <button className={`filter-btn ${selectedTradeTypes.has('External') ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedTradeTypes, 'External')}>{t('customDocumentsPage.filters.external', 'External')}</button>
            <button className={`filter-btn ${selectedTradeTypes.has('Splits') ? 'active' : ''}`} onClick={() => toggleFilter(setSelectedTradeTypes, 'Splits')}>{t('customDocumentsPage.filters.splits', 'Splits')}</button>
          </div>
        </div>
        {/* Clear Filters + Refresh */}
        <div className="cd-filter-actions">
          <button className="action-btn clear-btn" onClick={handleClearFilters}>{t('customDocumentsPage.filters.clearFilters', 'Clear Filters')}</button>
          <button className={`action-btn refresh-btn ${loadingTrades ? 'loading' : ''}`} disabled={loadingTrades} onClick={loadTrades}>
            {loadingTrades ? t('customDocumentsPage.filters.refreshing', 'Refreshing...') : t('customDocumentsPage.filters.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Trades Grid */}
      <div className="custom-docs-section">
        <div className="cd-section-header">
          <h3 className="custom-docs-section-title">{t('customDocumentsPage.tradesSection')}</h3>
          <div className="cd-toolbar-actions">
            <button className="cd-action-btn" onClick={() => gridRef.current?.api.selectAll()}>
              {t('customDocumentsPage.toolbar.selectAll', 'Select All')}
            </button>
            <button className="cd-action-btn" onClick={() => gridRef.current?.api.deselectAll()}>
              {t('customDocumentsPage.toolbar.deselectAll', 'Deselect All')}
            </button>
            <span className={`cd-selection-count ${selectionOverLimit ? 'over-limit' : ''}`}>
              {selectedTradeIds.length > 0 && (
                selectionOverLimit
                  ? t('customDocumentsPage.toolbar.selectedOverLimit', '{{count}} / {{max}} max', { count: selectedTradeIds.length, max: MAX_SELECTION })
                  : t('customDocumentsPage.toolbar.selected', '{{count}} selected', { count: selectedTradeIds.length })
              )}
            </span>
            <button
              className="btn-primary"
              disabled={!canGenerate}
              onClick={() => setShowGenerateModal(true)}
            >
              {t('customDocumentsPage.generateButton')}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowBatchModal(true)}
            >
              {t('batch.button', 'Batch Generate')}
            </button>
          </div>
        </div>
        {tradesError && <div className="config-error">{tradesError}</div>}
        {loadingTrades ? (
          <div className="tab-loading"><div className="spinner" /></div>
        ) : (
          <div className="ag-theme-alpine-dark custom-docs-grid">
            <AgGridReact
              ref={gridRef}
              rowData={filteredTrades}
              columnDefs={tradesColDefs}
              localeText={agGridLocale}
              rowSelection="multiple"
              onSelectionChanged={handleSelectionChanged}
              defaultColDef={{ resizable: true, sortable: true, filter: true, minWidth: 50 }}
              pagination={true}
              paginationPageSize={25}
              animateRows={true}
            />
          </div>
        )}
      </div>

      {/* Batch Jobs Section */}
      {batchJobs.length > 0 && (
        <div className="custom-docs-section batch-jobs-section">
          <div className="cd-section-header">
            <h3
              className="custom-docs-section-title batch-jobs-toggle"
              onClick={() => setBatchJobsExpanded(prev => !prev)}
            >
              {batchJobsExpanded ? '▾' : '▸'} {t('batch.jobsSection', 'Batch Jobs')}
              <span className="batch-jobs-count">({batchJobs.length})</span>
            </h3>
            <div className="cd-toolbar-actions">
              {selectedBatchJobId && (
                <button
                  className="cd-action-btn"
                  onClick={() => setSelectedBatchJobId(null)}
                >
                  {t('batch.clearFilter', 'Clear Filter')}
                </button>
              )}
              <button className="cd-action-btn" onClick={loadBatchJobs}>
                {t('batch.refreshJobs', 'Refresh Status')}
              </button>
            </div>
          </div>
          {batchJobsExpanded && (
            <div className="batch-jobs-grid">
              {batchJobs.map(job => {
                const progress = job.totalItems > 0
                  ? Math.round(((job.completedCount + job.failedCount) / job.totalItems) * 100)
                  : 0;
                const isSelected = selectedBatchJobId === job.jobId;
                return (
                  <div
                    key={job.jobId}
                    className={`batch-job-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedBatchJobId(isSelected ? null : job.jobId)}
                  >
                    <div className="batch-job-header">
                      <span className="batch-job-template">{job.templateName}</span>
                      <span className={`batch-job-status ${job.status.toLowerCase().replace(/_/g, '-')}`}>
                        {t(`batch.status.${job.status}`, job.status)}
                      </span>
                      <button
                        className="batch-job-dismiss"
                        title={t('batch.dismiss', 'Dismiss')}
                        onClick={e => {
                          e.stopPropagation();
                          customDocumentService.dismissBatchJob(clientId!, job.jobId)
                            .then(() => {
                              setBatchJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                              if (selectedBatchJobId === job.jobId) setSelectedBatchJobId(null);
                            })
                            .catch(() => showToastNotification(t('batch.dismissError', 'Failed to dismiss job'), 'error'));
                        }}
                      >
                        &times;
                      </button>
                    </div>
                    <div className="batch-job-progress-bar">
                      <div
                        className="batch-job-progress-fill"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="batch-job-details">
                      <span>{job.completedCount}/{job.totalItems} {t('batch.completed', 'completed')}</span>
                      {job.failedCount > 0 && (
                        <span className="batch-job-failed">{job.failedCount} {t('batch.failed', 'failed')}</span>
                      )}
                      <span className="batch-job-format">{job.outputFormat}</span>
                    </div>
                    <div className="batch-job-meta">
                      <span>{new Date(job.createdAt).toLocaleString('en-GB', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}</span>
                      <span>{job.createdByName}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Generated Documents Grid */}
      <div className="custom-docs-section">
        <div className="cd-section-header">
          <h3 className="custom-docs-section-title">{t('customDocumentsPage.docsSection')}</h3>
          <input
            type="text"
            className="cd-docs-search-input"
            placeholder={t('customDocumentsPage.docsSearchPlaceholder', 'Search documents...')}
            value={docsSearchText}
            onChange={e => setDocsSearchText(e.target.value)}
          />
          <div className="cd-toolbar-actions">
            <button className="cd-action-btn" onClick={() => docsGridRef.current?.api.selectAll()}>
              {t('customDocumentsPage.toolbar.selectAll', 'Select All')}
            </button>
            <button className="cd-action-btn" onClick={() => docsGridRef.current?.api.deselectAll()}>
              {t('customDocumentsPage.toolbar.deselectAll', 'Deselect All')}
            </button>
            {selectedDocRecords.length > 0 && (
              <>
                <span className="cd-selection-count">
                  {t('customDocumentsPage.docsGrid.docsSelected', '{{count}} selected', { count: selectedDocRecords.length })}
                </span>
                <button
                  className="cd-action-btn"
                  disabled={selectedDocRecords.filter(r => r.status === 'SUCCESS').length === 0}
                  onClick={handleDownloadAll}
                >
                  {t('customDocumentsPage.docsGrid.downloadSelected', 'Download Selected')}
                </button>
                <button
                  className="cd-action-btn cd-danger"
                  onClick={() => setShowDeleteAllConfirm(true)}
                >
                  {t('customDocumentsPage.docsGrid.deleteSelected', 'Delete Selected')}
                </button>
              </>
            )}
          </div>
        </div>
        {loadingRecords ? (
          <div className="tab-loading"><div className="spinner" /></div>
        ) : (
          <div className="ag-theme-alpine-dark custom-docs-grid">
            <AgGridReact
              ref={docsGridRef}
              rowData={filteredAuditRecords}
              columnDefs={docsColDefs}
              localeText={agGridLocale}
              rowSelection="multiple"
              onSelectionChanged={handleDocsSelectionChanged}
              defaultColDef={{ resizable: true, sortable: true, filter: true, minWidth: 50 }}
              quickFilterText={docsSearchText}
              pagination={true}
              paginationPageSize={25}
              animateRows={true}
            />
          </div>
        )}
      </div>

      {/* Generation Modal */}
      {showGenerateModal && clientId && (
        <GenerateDocumentModal
          clientId={clientId}
          selectedTradeIds={selectedTradeIds}
          templates={templates}
          onGenerated={handleGenerated}
          onClose={() => setShowGenerateModal(false)}
        />
      )}

      {/* Batch Generation Modal */}
      {showBatchModal && clientId && (
        <BatchGenerateModal
          clientId={clientId}
          templates={templates}
          onJobCreated={handleBatchJobCreated}
          onClose={() => setShowBatchModal(false)}
        />
      )}

      <UploadingModal isOpen={!!busyMessage} message={busyMessage || ''} />

      {/* Delete Confirm (single) */}
      <ConfirmDialog
        isOpen={!!deleteRecordId}
        title={t('customDocumentsPage.deleteConfirm.title')}
        message={t('customDocumentsPage.deleteConfirm.message')}
        confirmLabel={t('customDocumentsPage.deleteConfirm.confirm')}
        onConfirm={handleDeleteRecord}
        onCancel={() => setDeleteRecordId(null)}
      />

      {/* Delete Selected Confirm */}
      <ConfirmDialog
        isOpen={showDeleteAllConfirm}
        title={t('customDocumentsPage.deleteSelectedConfirm.title', 'Delete Documents')}
        message={t('customDocumentsPage.deleteSelectedConfirm.message', 'Are you sure you want to delete {{count}} documents? The files will be permanently removed.', { count: selectedDocRecords.length })}
        confirmLabel={t('customDocumentsPage.deleteSelectedConfirm.confirm', 'Delete Selected')}
        onConfirm={handleDeleteAll}
        onCancel={() => setShowDeleteAllConfirm(false)}
      />

      {/* Toast */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={5000}
      />
    </div>
  );
};

export default CustomDocuments;
